/* eslint-disable id-blacklist */
import { Model } from 'app/model/backendmodel';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { ColumnMappingPair } from './../../model/pipeline/mappingpair';

import { ISelectOption } from 'app/interfaces/ISelectOption';
import { NamedArray } from 'app/model/base/namedarray';
import { Column } from 'app/model/dataset/column';
import { Owned } from 'app/model/base/named';
import { DataFlowDataType } from '../../model/dataflow/dataflow-datatype';
import moment from 'moment';
import { IStructure } from 'app/model/adapters/datasets/dataset.constant.adapter';
import shellapi from 'a365-shell-api';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { cloneDeep } from 'lodash';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';

// this should be logical type
export const enum ColumnType {
    Byte = 'Byte',
    Int16 = 'Int16',
    Int32 = 'Int32',
    Int64 = 'Int64',
    Single = 'Single',
    Double = 'Double',
    Decimal = 'Decimal',
    ByteArray = 'Byte[]',
    Boolean = 'Boolean',
    String = 'String',
    Guid = 'Guid',
    DateTime = 'DateTime',
    Date = 'Date',
    DateTimeOffset = 'DateTimeOffset',
    TimeSpan = 'TimeSpan',
    Object = 'Object',
    UnsupportedType = 'Unsupported Type',
    SByte = 'SByte',
    UInt16 = 'UInt16',
    UInt32 = 'UInt32',
    UInt64 = 'UInt64',
    // Delta lake
    INT = 'Int',
    FLOAT = 'Float',
    Array = 'Array', // M365
    Time = 'Time'
}

export const enum ColumnPhysicalType {
    Boolean = 'boolean',
    Byte = 'byte',
    Short = 'short',
    Binary = 'binary',
    BinaryFloat = 'binary_float',
    BinaryDouble = 'binary_double',
    Blob = 'blob',
    Clob = 'clob',
    JSON = 'json',
    Date = 'date',
    Timestamp = 'timestamp',
    TimestampTimeZoneFull = 'timestamp with time zone',
    TimestampLocalTimeZoneFull = 'timestamp with local time zone',
    TimestampTimeZone = 'timestamptz',
    TimestampLocalTimeZone = 'timestampltz',
    Timestamp_ntz = 'timestamp_ntz',
    Timestamp_tz = 'timestamp_tz',
    Timestamp_ltz = 'timestamp_ltz',
    Decimal = 'decimal',
    Double = 'double',
    Float = 'float',
    String = 'string',
    Integer = 'integer',
    Long = 'long',
    Bit = 'bit',
    Tinyint = 'tinyint',
    Smallint = 'smallint',
    Int = 'int',
    Bigint = 'bigint',
    Int32 = 'int32',
    Int64 = 'int64',
    Smalldatetime = 'smalldatetime',
    Datetime = 'datetime',
    Datetime2 = 'datetime2',
    Datetimeoffset = 'datetimeoffset',
    IntervalDaySecondFull = 'interval day to second',
    IntervalYearMonthFull = 'interval year to month',
    IntervalDaySecond = 'intervalds',
    IntervalYearMonth = 'intervalym',
    Time = 'time',
    Smallmoney = 'smallmoney',
    Money = 'money',
    Numeric = 'numeric',
    Bignumeric = 'bignumeric',
    Number = 'number',
    Real = 'real',
    Uniqueidentifier = 'uniqueidentifier',
    Sql_variant = 'sql_variant',
    Xml = 'xml',
    Image = 'image',
    Varbinary = 'varbinary',
    Nchar = 'nchar',
    Char = 'char',
    Nvarchar = 'nvarchar',
    Varchar = 'varchar',
    Varchar2 = 'varchar2',
    Nvarchar2 = 'nvarchar2',
    Text = 'text',
    Ntext = 'ntext',
    Hierarchyid = 'hierarchyid',
    Geometry = 'geometry',
    Geography = 'geography',
    Bytes = 'bytes'
}

export const defaultSupportedColumnTypes: string[] = [
    ColumnType.Byte,
    ColumnType.Int16,
    ColumnType.Int32,
    ColumnType.Int64,
    ColumnType.Single,
    ColumnType.Double,
    ColumnType.Decimal,
    ColumnType.ByteArray,
    ColumnType.Boolean,
    ColumnType.String,
    ColumnType.Guid,
    ColumnType.DateTime,
    ColumnType.DateTimeOffset,
    ColumnType.TimeSpan
];

export const extendedColumnTypes: string[] = [
    ...defaultSupportedColumnTypes,
    ColumnType.SByte,
    ColumnType.UInt16,
    ColumnType.UInt32,
    ColumnType.UInt64
];

export const complexTypeSupportedColumnTypes: string[] = [
    ColumnType.Boolean,
    ColumnType.Double,
    ColumnType.Int16,
    ColumnType.Int32,
    ColumnType.Int64,
    ColumnType.String,
    ColumnType.Single,
    ColumnType.DateTime
];

// The sequence of this array must align with the below matrix
const allColumnTypes: string[] = [
    ColumnType.Byte,
    ColumnType.Int16,
    ColumnType.Int32,
    ColumnType.Int64,
    ColumnType.Single,
    ColumnType.Double,
    ColumnType.Decimal,
    ColumnType.ByteArray,
    ColumnType.Boolean,
    ColumnType.String,
    ColumnType.Guid,
    ColumnType.DateTime,
    ColumnType.DateTimeOffset,
    ColumnType.TimeSpan,
    ColumnType.Object,
    ColumnType.Date,
    ColumnType.SByte,
    ColumnType.UInt16,
    ColumnType.UInt32,
    ColumnType.UInt64,
    ColumnType.Time
];

// 1 means always compatible, 0 means not sure, 2 means always not compatible
const byteToOthers: number[] = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 1, 2, 1, 1, 1, 1, 2];
const int16ToOthers: number[] = [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 1, 2, 0, 1, 1, 1, 2];
const int32ToOthers: number[] = [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 1, 1, 2, 0, 0, 1, 1, 2];
const int64ToOthers: number[] = [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 1, 1, 2, 0, 0, 0, 1, 2];
const singleToOthers: number[] = [0, 0, 0, 0, 1, 1, 0, 1, 1, 1, 2, 2, 2, 2, 1, 2, 0, 0, 0, 0, 2];
const doubleToOthers: number[] = [0, 0, 0, 0, 0, 1, 0, 1, 1, 1, 2, 2, 2, 2, 1, 2, 0, 0, 0, 0, 2];
const decimalToOthers: number[] = [0, 0, 0, 0, 0, 0, 1, 2, 1, 1, 2, 2, 2, 2, 1, 2, 0, 0, 0, 0, 2];
const bytearrayToOthers: number[] = [2, 2, 2, 2, 2, 2, 2, 0, 2, 1, 2, 2, 2, 2, 1, 2, 2, 2, 2, 2, 2];
const booleanToOthers: number[] = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 1, 2, 1, 1, 1, 1, 2];
const stringToOthers: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1];
const guidToOthers: number[] = [2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 2, 2, 2, 1, 2, 2, 2, 2, 2, 2];
const datetimeToOthers: number[] = [2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 1, 0, 2, 1, 0, 2, 2, 2, 2, 1];
const datetimeoffsetToOthers: number[] = [2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 2, 1, 2, 1, 2, 2, 2, 2, 2, 1];
const timespanToOthers: number[] = [2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 2, 2, 1, 1, 2, 2, 2, 2, 2, 2];
const objectToOthers: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2];
const dateToOthers: number[] = [2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 1, 0, 2, 1, 1, 2, 2, 2, 2, 2]; // string/ dateTime/ datetimeoffset / object
const sbyteToOthers: number[] = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 1, 2, 1, 1, 1, 1, 2];
const uint16ToOthers: number[] = [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 1, 2, 0, 1, 1, 1, 2];
const uint32ToOthers: number[] = [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 1, 1, 2, 0, 0, 1, 1, 2];
const uint64ToOthers: number[] = [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 1, 1, 2, 0, 0, 0, 1, 2];
const timeToOthers: number[] = [2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 1, 1, 2, 2, 2, 2, 2, 2, 2, 1];

const columnTypeCompatibleArray: number[][] = [
    byteToOthers,
    int16ToOthers,
    int32ToOthers,
    int64ToOthers,
    singleToOthers,
    doubleToOthers,
    decimalToOthers,
    bytearrayToOthers,
    booleanToOthers,
    stringToOthers,
    guidToOthers,
    datetimeToOthers,
    datetimeoffsetToOthers,
    timespanToOthers,
    objectToOthers,
    dateToOthers,
    sbyteToOthers,
    uint16ToOthers,
    uint32ToOthers,
    uint64ToOthers,
    timeToOthers
];

// The sequence of this array must align with the below matrix
export const typeConversionSupportedColumnTypes: string[] = [
    ColumnType.Boolean,
    ColumnType.SByte,
    ColumnType.Byte,
    ColumnType.Int16,
    ColumnType.UInt16,
    ColumnType.Int32,
    ColumnType.UInt32,
    ColumnType.Int64,
    ColumnType.UInt64,
    ColumnType.Single,
    ColumnType.Double,
    ColumnType.Decimal,
    ColumnType.String,
    ColumnType.DateTime,
    ColumnType.DateTimeOffset,
    ColumnType.TimeSpan,
    ColumnType.Guid,
    ColumnType.ByteArray,
    ColumnType.Date,
    ColumnType.Time
];

export const sqlBinaryPhysicalTypes: string[] = [
    ColumnPhysicalType.Binary,
    ColumnPhysicalType.Varbinary,
    ColumnPhysicalType.Char,
    ColumnPhysicalType.Nchar,
    ColumnPhysicalType.Nvarchar,
    ColumnPhysicalType.Varchar
];
export const binaryPhysicalTypes: string[] = [
    ...sqlBinaryPhysicalTypes,
    ColumnPhysicalType.Nvarchar2,
    ColumnPhysicalType.Varchar2
];

/*
    T, may have data truncation during run time
    ST, must have data truncation during run time
    IN, incompatible
    C, compatible
*/
const typeConversionBooleanToOthersArray: string[] = [
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionSByteToOthersArray: string[] = [
    'T',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionByteToOthersArray: string[] = [
    'T',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionInt16ToOthersArray: string[] = [
    'T',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionUInt16ToOthersArray: string[] = [
    'T',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionInt32ToOthersArray: string[] = [
    'T',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'T',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionUInt32ToOthersArray: string[] = [
    'T',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'T',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionInt64ToOthersArray: string[] = [
    'T',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'T',
    'T',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionUInt64ToOthersArray: string[] = [
    'T',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'T',
    'T',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionSingleToOthersArray: string[] = [
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'C',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionDoubleToOthersArray: string[] = [
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionDecimalToOthersArray: string[] = [
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'T',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionStringToOthersArray: string[] = [
    'T',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'C',
    'T',
    'T',
    'T',
    'C',
    'C',
    'C',
    'C'
];
const typeConversionDateTimeToOthersArray: string[] = [
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'C',
    'C'
];
const typeConversionDateTimeOffsetToOthersArray: string[] = [
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'C',
    'ST',
    'C',
    'IN',
    'IN',
    'IN',
    'C',
    'C'
];
const typeConversionTimeSpanToOthersArray: string[] = [
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'C',
    'IN',
    'IN',
    'C',
    'IN',
    'IN',
    'IN',
    'IN'
];
const typeConversionGuidToOthersArray: string[] = [
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'C',
    'IN',
    'IN',
    'IN',
    'C',
    'IN',
    'IN',
    'IN'
];
const typeConversionByteArrayToOthersArray: string[] = [
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'C',
    'IN',
    'IN'
];
const typeConversionDateToOtherArray: string[] = [
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'C',
    'C',
    'C',
    'IN', //ColumnType.TimeSpan,
    'IN',
    'IN',
    'C',
    'IN'
];
const typeConversionTimeToOtherArray: string[] = [
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'IN',
    'C',
    'C',
    'C',
    'IN',
    'IN',
    'IN',
    'IN',
    'C'
];

export const typeConversionCompatabileMatrix: string[][] = [
    typeConversionBooleanToOthersArray,
    typeConversionSByteToOthersArray,
    typeConversionByteToOthersArray,
    typeConversionInt16ToOthersArray,
    typeConversionUInt16ToOthersArray,
    typeConversionInt32ToOthersArray,
    typeConversionUInt32ToOthersArray,
    typeConversionInt64ToOthersArray,
    typeConversionUInt64ToOthersArray,
    typeConversionSingleToOthersArray,
    typeConversionDoubleToOthersArray,
    typeConversionDecimalToOthersArray,
    typeConversionStringToOthersArray,
    typeConversionDateTimeToOthersArray,
    typeConversionDateTimeOffsetToOthersArray,
    typeConversionTimeSpanToOthersArray,
    typeConversionGuidToOthersArray,
    typeConversionByteArrayToOthersArray,
    typeConversionDateToOtherArray,
    typeConversionTimeToOtherArray
];

export function checkTypeConversionCompatibility(
    sourceType: string,
    destinationType: string,
    sourceScale?: number,
    destinationScale?: number
): string {
    if (sourceType === ColumnType.UnsupportedType || destinationType === ColumnType.UnsupportedType) {
        return 'IN';
    }
    if (sourceType === ColumnType.Decimal && destinationType === ColumnType.Decimal) {
        if (sourceScale != null && destinationScale != null && sourceScale > destinationScale) {
            return 'ST';
        }
    }
    const i = typeConversionSupportedColumnTypes.indexOf(sourceType);
    const j = typeConversionSupportedColumnTypes.indexOf(destinationType);
    return i < 0 || j < 0 ? 'C' : typeConversionCompatabileMatrix[i][j];
}

export function isNotCompatibleColumnType(sourceType: string, destinationType: string): boolean {
    if (sourceType === ColumnType.UnsupportedType || destinationType === ColumnType.UnsupportedType) {
        return true;
    }
    const i = allColumnTypes.indexOf(sourceType);
    const j = allColumnTypes.indexOf(destinationType);
    return i < 0 || j < 0 ? false : columnTypeCompatibleArray[i][j] === 2;
}

export function getColumnsWithSupportType(
    owner: Owned,
    columns: NamedArray<Column>,
    supportTypeList: string[]
): NamedArray<Column> {
    const result = NamedArray.create<Column>(owner);
    columns
        .filter(
            (column) =>
                column.columnType == null ||
                supportTypeList.filter((t) => t.toLowerCase() === column.columnType.toLowerCase()).length > 0
        )
        .forEach((column) => result.push(column));
    return result;
}

export function getColumnsWithUnsupportType(columns: NamedArray<Column>, supportTypeList: string[]): Column[] {
    return columns.filter(
        (column) =>
            column.columnType &&
            supportTypeList.filter((t) => t.toLowerCase() === column.columnType.toLowerCase()).length <= 0
    );
}

export function columnWithDateRelatedType(column: Column): boolean {
    return (
        column.columnType === ColumnType.Date ||
        column.columnType === ColumnType.DateTime ||
        column.columnType === ColumnType.DateTimeOffset
    );
}

export function detectColumnType(value: string): ColumnType {
    // roughly detect data format
    if (/^\-?(\d+|0x[\dA-Fa-f]+)$/.test(value)) {
        return ColumnType.Int64;
    }
    if (/^\-?\d+\.\d+$/.test(value)) {
        return ColumnType.Decimal;
    }
    if (moment(value).isValid()) {
        return ColumnType.DateTime;
    }
    return ColumnType.String;
}

export const logicalTypeOptions: Array<ISelectOption<ColumnType | DataFlowDataType | undefined>> = [
    {
        label: stringResources.Default,
        value: undefined,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnType.Byte,
        value: ColumnType.Byte,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnType.SByte,
        value: ColumnType.SByte,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnType.ByteArray,
        value: ColumnType.ByteArray,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnType.Boolean,
        value: ColumnType.Boolean,
        labelIcon: 'arg-type-icon svg-i-16x16-Bool',
        labelIconRight: true
    },
    {
        label: ColumnType.Date,
        value: ColumnType.Date,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnType.DateTime,
        value: ColumnType.DateTime,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnType.DateTimeOffset,
        value: ColumnType.DateTimeOffset,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnType.Decimal,
        value: ColumnType.Decimal,
        labelIcon: 'arg-type-icon svg-i-16x16-Decimal',
        labelIconRight: true
    },
    {
        label: ColumnType.Double,
        value: ColumnType.Double,
        labelIcon: 'arg-type-icon svg-i-16x16-Double',
        labelIconRight: true
    },
    {
        label: ColumnType.Guid,
        value: ColumnType.Guid,
        labelIcon: 'arg-type-icon svg-i-16x16-String',
        labelIconRight: true
    },
    {
        label: ColumnType.Int16,
        value: ColumnType.Int16,
        labelIcon: 'arg-type-icon svg-i-16x16-short',
        labelIconRight: true
    },
    {
        label: ColumnType.Int32,
        value: ColumnType.Int32,
        labelIcon: 'arg-type-icon svg-i-16x16-Int',
        labelIconRight: true
    },
    {
        label: ColumnType.Int64,
        value: ColumnType.Int64,
        labelIcon: 'arg-type-icon svg-i-16x16-long',
        labelIconRight: true
    },
    {
        label: ColumnType.UInt16,
        value: ColumnType.UInt16,
        labelIcon: 'arg-type-icon svg-i-16x16-short',
        labelIconRight: true
    },
    {
        label: ColumnType.UInt32,
        value: ColumnType.UInt32,
        labelIcon: 'arg-type-icon svg-i-16x16-Int',
        labelIconRight: true
    },
    {
        label: ColumnType.UInt64,
        value: ColumnType.UInt64,
        labelIcon: 'arg-type-icon svg-i-16x16-long',
        labelIconRight: true
    },
    {
        label: ColumnType.FLOAT,
        value: ColumnType.FLOAT,
        labelIcon: 'arg-type-icon svg-i-16x16-float',
        labelIconRight: true
    },
    {
        label: ColumnType.Single,
        value: ColumnType.Single,
        labelIcon: 'arg-type-icon svg-i-16x16-float',
        labelIconRight: true
    },
    {
        label: ColumnType.String,
        value: ColumnType.String,
        labelIcon: 'arg-type-icon svg-i-16x16-String',
        labelIconRight: true
    },
    {
        label: ColumnType.TimeSpan,
        value: ColumnType.TimeSpan,
        labelIcon: 'arg-type-icon svg-i-16x16-Timestamp',
        labelIconRight: true
    },
    {
        label: ColumnType.Object,
        value: ColumnType.Object,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnType.Array,
        value: ColumnType.Array,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnType.Time,
        value: ColumnType.Time,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    }
];

export const physicalTypeOptions: Array<ISelectOption<ColumnPhysicalType | undefined>> = [
    {
        label: stringResources.Default,
        value: undefined,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Boolean,
        value: ColumnPhysicalType.Boolean,
        labelIcon: 'arg-type-icon svg-i-16x16-Bool',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Byte,
        value: ColumnPhysicalType.Byte,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Bytes,
        value: ColumnPhysicalType.Bytes,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Short,
        value: ColumnPhysicalType.Short,
        labelIcon: 'arg-type-icon svg-i-16x16-short',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Binary,
        value: ColumnPhysicalType.Binary,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Date,
        value: ColumnPhysicalType.Date,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Timestamp,
        value: ColumnPhysicalType.Timestamp,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Timestamp_ntz,
        value: ColumnPhysicalType.Timestamp_ntz,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Timestamp_tz,
        value: ColumnPhysicalType.Timestamp_tz,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Timestamp_ltz,
        value: ColumnPhysicalType.Timestamp_ltz,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Decimal,
        value: ColumnPhysicalType.Decimal,
        labelIcon: 'arg-type-icon svg-i-16x16-Decimal',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Double,
        value: ColumnPhysicalType.Double,
        labelIcon: 'arg-type-icon svg-i-16x16-Double',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Float,
        value: ColumnPhysicalType.Float,
        labelIcon: 'arg-type-icon svg-i-16x16-float',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.String,
        value: ColumnPhysicalType.String,
        labelIcon: 'arg-type-icon svg-i-16x16-String',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Integer,
        value: ColumnPhysicalType.Integer,
        labelIcon: 'arg-type-icon svg-i-16x16-Int',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Long,
        value: ColumnPhysicalType.Long,
        labelIcon: 'arg-type-icon svg-i-16x16-long',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Bit,
        value: ColumnPhysicalType.Bit,
        labelIcon: 'arg-type-icon svg-i-16x16-Bool',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Tinyint,
        value: ColumnPhysicalType.Tinyint,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Smallint,
        value: ColumnPhysicalType.Smallint,
        labelIcon: 'arg-type-icon svg-i-16x16-Int',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Int,
        value: ColumnPhysicalType.Int,
        labelIcon: 'arg-type-icon svg-i-16x16-Int',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Bigint,
        value: ColumnPhysicalType.Bigint,
        labelIcon: 'arg-type-icon svg-i-16x16-Int',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Smalldatetime,
        value: ColumnPhysicalType.Smalldatetime,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Datetime,
        value: ColumnPhysicalType.Datetime,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Datetime2,
        value: ColumnPhysicalType.Datetime2,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Datetimeoffset,
        value: ColumnPhysicalType.Datetimeoffset,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Time,
        value: ColumnPhysicalType.Time,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Smallmoney,
        value: ColumnPhysicalType.Smallmoney,
        labelIcon: 'arg-type-icon svg-i-16x16-Decimal',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Money,
        value: ColumnPhysicalType.Money,
        labelIcon: 'arg-type-icon svg-i-16x16-Decimal',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Numeric,
        value: ColumnPhysicalType.Numeric,
        labelIcon: 'arg-type-icon svg-i-16x16-Decimal',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Bignumeric,
        value: ColumnPhysicalType.Bignumeric,
        labelIcon: 'arg-type-icon svg-i-16x16-Decimal',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Real,
        value: ColumnPhysicalType.Real,
        labelIcon: 'arg-type-icon svg-i-16x16-float',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Uniqueidentifier,
        value: ColumnPhysicalType.Uniqueidentifier,
        labelIcon: 'arg-type-icon svg-i-16x16-String', // To do：use icon Guid
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Sql_variant,
        value: ColumnPhysicalType.Sql_variant,
        labelIcon: 'arg-type-icon svg-i-16x16-Any', // To do：use icon Object
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Xml,
        value: ColumnPhysicalType.Xml,
        labelIcon: 'arg-type-icon svg-i-16x16-String',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Image,
        value: ColumnPhysicalType.Image,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Varbinary,
        value: ColumnPhysicalType.Varbinary,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Nchar,
        value: ColumnPhysicalType.Nchar,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Char,
        value: ColumnPhysicalType.Char,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Nvarchar,
        value: ColumnPhysicalType.Nvarchar,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Varchar,
        value: ColumnPhysicalType.Varchar,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Text,
        value: ColumnPhysicalType.Text,
        labelIcon: 'arg-type-icon svg-i-16x16-String',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Ntext,
        value: ColumnPhysicalType.Ntext,
        labelIcon: 'arg-type-icon svg-i-16x16-String',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Hierarchyid,
        value: ColumnPhysicalType.Hierarchyid,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Geometry,
        value: ColumnPhysicalType.Geometry,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Geography,
        value: ColumnPhysicalType.Geography,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.BinaryDouble,
        value: ColumnPhysicalType.BinaryDouble,
        labelIcon: 'arg-type-icon svg-i-16x16-Double',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.BinaryFloat,
        value: ColumnPhysicalType.BinaryFloat,
        labelIcon: 'arg-type-icon svg-i-16x16-float',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.TimestampTimeZone,
        value: ColumnPhysicalType.TimestampTimeZone,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.TimestampTimeZoneFull,
        value: ColumnPhysicalType.TimestampTimeZoneFull,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.TimestampLocalTimeZoneFull,
        value: ColumnPhysicalType.TimestampLocalTimeZoneFull,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.TimestampLocalTimeZone,
        value: ColumnPhysicalType.TimestampLocalTimeZone,
        labelIcon: 'arg-type-icon svg-i-16x16-Date',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.IntervalDaySecond,
        value: ColumnPhysicalType.IntervalDaySecond,
        labelIcon: 'arg-type-icon svg-i-16x16-Timestamp',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.IntervalDaySecondFull,
        value: ColumnPhysicalType.IntervalDaySecondFull,
        labelIcon: 'arg-type-icon svg-i-16x16-Timestamp',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.IntervalYearMonthFull,
        value: ColumnPhysicalType.IntervalYearMonthFull,
        labelIcon: 'arg-type-icon svg-i-16x16-Timestamp',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.IntervalYearMonth,
        value: ColumnPhysicalType.IntervalYearMonth,
        labelIcon: 'arg-type-icon svg-i-16x16-Timestamp',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Number,
        value: ColumnPhysicalType.Number,
        labelIcon: 'arg-type-icon svg-i-16x16-Decimal',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Nvarchar2,
        value: ColumnPhysicalType.Nvarchar2,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Varchar2,
        value: ColumnPhysicalType.Varchar2,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Blob,
        value: ColumnPhysicalType.Blob,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.Clob,
        value: ColumnPhysicalType.Clob,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    },
    {
        label: ColumnPhysicalType.JSON,
        value: ColumnPhysicalType.JSON,
        labelIcon: 'arg-type-icon svg-i-16x16-Any',
        labelIconRight: true
    }
];

export const avroPhysicalToLogicalMapping: { [key: string]: string } = {
    int: ColumnType.Int32,
    long: ColumnType.Int64,
    float: ColumnType.Single,
    double: ColumnType.Double,
    bytes: ColumnType.ByteArray,
    string: ColumnType.String,
    boolean: ColumnType.Boolean,
    record: ColumnType.UnsupportedType,
    enum: ColumnType.UnsupportedType,
    array: ColumnType.UnsupportedType,
    maps: ColumnType.UnsupportedType,
    unions: ColumnType.UnsupportedType,
    fixed: ColumnType.ByteArray
};

export const supportLakehouseSinkParititionTypes: string[] = [
    ColumnType.String,
    ColumnType.Int16,
    ColumnType.Int32,
    ColumnType.Int64,
    ColumnType.Boolean,
    ColumnType.Guid,
    ColumnType.Date,
    ColumnType.DateTime,
    ColumnType.DateTimeOffset
];

// eslint-disable-next-line
export const cosmosSystemColumns: Array<{ name: string; type: string }> = [
    { name: 'id', type: 'string' },
    { name: '_rid', type: 'string' },
    { name: '_self', type: 'string' },
    { name: '_attachments', type: 'string' },
    { name: '_ts', type: 'timestamp' },
    { name: '_etag', type: 'string' }
];

export function convertMappingToStructure(mappings: ColumnMappingPair[], forSink?: boolean): IStructure[] {
    return mappings
        .filter((pair) => {
            const p = !forSink ? pair.source : pair.sink;
            if (stringIsNullOrEmpty(p.name) && p.index == null) {
                return false;
            }
            return true;
        })
        .map((pair) => {
            const p = !forSink ? pair.source : pair.sink;
            const isBigDecimal = p.precision != null && p.scale != null;
            return {
                name: p.name || '_c' + (p.index - 1),
                type: isBigDecimal ? undefined : p.columnType,
                ordinal: p.index,
                format: isBigDecimal ? undefined : p.format,
                culture: isBigDecimal ? undefined : p.culture
            };
        });
}

export function getPhysicalSchemaFromIColumnForDataset(
    physicalSchema: shellapi.queryAPI.IColumn[],
    needColumnName: boolean
): IStructure[] {
    if (!physicalSchema || physicalSchema.length === 0) {
        return [];
    }
    return physicalSchema
        .map((column) => {
            if (!column.isAdditionalColumn) {
                const columnValue: IStructure = {
                    name: needColumnName ? column.name : undefined,
                    type: column.nativeDataType
                };
                if (column.description) {
                    columnValue.description = column.description;
                }
                // same as Madrid
                columnValue.precision = column.precision === 255 ? undefined : column.precision; // TODO (najian): remove the hardcode judge, backend API should return null.
                columnValue.scale = column.scale === 255 ? undefined : column.scale;
                return columnValue;
            }
            return undefined;
        })
        .filter((column) => !!column);
}

export function getPhysicalHierarchicalSchemaForDataset(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hierarchicalSchema: { [key: string]: any },
    datasetType?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { [key: string]: any } {
    // same as Madrid, hardcode to exclude system columns for cosmos
    if (
        hierarchicalSchema != null &&
        datasetType === adfModelTypeMap.cosmosSql.datasetType &&
        hierarchicalSchema.properties != null
    ) {
        const result = cloneDeep(hierarchicalSchema);
        cosmosSystemColumns.forEach((col) => delete result.properties[col.name]);
        return result;
    }
    return hierarchicalSchema || {};
}

export function translatorToMappingPairModel(mappings: Model.MappingPair[]): ColumnMappingPair[] {
    if (!arrayIsNullOrEmpty(mappings)) {
        const result: ColumnMappingPair[] = [];
        const toMappingItem = (col: Model.MappingItem): Column => {
            const result = new Column();
            if (col != null) {
                result.name = col.name;
                result.columnType = col.type;
                result.format = col.format;
                result.index = col.ordinal;
                result.path = col.path;
                result.culture = col.culture;
                result.physicalType = col.physicalType;
                result.scale = col.scale;
                result.precision = col.precision;
                result.size = col.length;
                result.unit = col.unit;
                result.parquetPorperties = col.parquetProperties;
                result.isNullable = col.isNullable;
            }
            return result;
        };
        mappings.forEach((pair) => {
            const newPair = new ColumnMappingPair();
            newPair.source = toMappingItem(pair.source);
            newPair.sink = toMappingItem(pair.sink);
            result.push(newPair);
        });
        return result;
    }
    return [];
}

export function getIconFromLogicalType(columnType: string): string {
    if (!columnType) {
        return '';
    }
    return (logicalTypeOptions.find((opt) => opt.value === columnType) || {}).labelIcon || '';
}

export function getIconFromPhysicalType(physicalType: string): string {
    if (!physicalType) {
        return '';
    }
    return (physicalTypeOptions.find((opt) => opt.value === physicalType.toLowerCase()) || {}).labelIcon || '';
}

export const supportRowVersionWatermark = (datasetType: string) =>
    [
        adfModelTypeMap.azureSql.datasetType,
        adfModelTypeMap.sqlServer.datasetType,
        adfModelTypeMap.azureSqlMI.datasetType,
        adfModelTypeMap.azureSqlDW.datasetType,
        adfModelTypeMap.fabricSql.datasetType
    ].includes(datasetType);

export const isWaterMarkColumnType = (
    {
        type: columnType,
        nativeDataType: physicalType
    }: {
        type: string;
        nativeDataType?: string;
    },
    datasetMetadata?: IDatasetTypeMetadata
): boolean => {
    const datasetType = datasetMetadata?.type;

    if (datasetType === adfModelTypeMap.sapTable.datasetType) {
        /**
         * | SAP ABAP Type | Service interim data type |
         * | -------- | -------- |
         * | C (String) | String |
         * | I (Integer) | Int32 |
         * | F (Float) | Double |
         * | D (Date) | String |
         * | T (Time) | String |
         * | P (BCD Packed, Currency, Decimal, Qty) | Decimal |
         * | N (Numeric) | String |
         * | X (Binary and Raw) | String |
         */
        return !stringIsNullOrEmpty(physicalType) && physicalType !== 'X' && physicalType !== 'C';
    }

    return (
        (supportRowVersionWatermark(datasetType) && physicalType === 'timestamp') ||
        columnType === ColumnType.Date ||
        columnType === ColumnType.DateTime ||
        columnType === ColumnType.DateTimeOffset ||
        columnType === ColumnType.Int16 ||
        columnType === ColumnType.Int32 ||
        columnType === ColumnType.Int64 ||
        columnType === ColumnType.Decimal ||
        columnType === ColumnType.UInt16 ||
        columnType === ColumnType.UInt32 ||
        columnType === ColumnType.UInt64 ||
        (datasetMetadata?.copyJob?.incrementalColumnConversion?.supportConversion &&
            datasetMetadata.copyJob.incrementalColumnConversion.conversionMapping[ColumnType.String] != null &&
            columnType === ColumnType.String)
    );
};

export const isIntegerType = (
    { type: columnType, physicalType }: { type: string; physicalType: string },
    datasetType?: string
): boolean => {
    return (
        ((datasetType === adfModelTypeMap.oracle.datasetType ||
            datasetType === adfModelTypeMap.amazonRdsForOracle.datasetType) &&
            physicalType === 'NUMBER') ||
        columnType === ColumnType.Int16 ||
        columnType === ColumnType.Int32 ||
        columnType === ColumnType.Int64 ||
        columnType === ColumnType.UInt16 ||
        columnType === ColumnType.UInt32 ||
        columnType === ColumnType.UInt64
    );
};

/**
 * Returns true if the column can be used as one of the two columns of the
 * "Created + modified" incremental column pattern.
 */
export const isMultiColumnCompatiType = (
    columnType?: string,
    nativeDataType?: string,
    datasetType?: string
): boolean => {
    if (datasetType === adfModelTypeMap.sapTable.datasetType) {
        // SAP Table maps ABAP time (`T`) and date (`D`) to logical type String, so check the ABAP type.
        return nativeDataType === 'T' || nativeDataType === 'D';
    }
    return (
        columnType === ColumnType.DateTime ||
        columnType === ColumnType.DateTimeOffset ||
        (supportRowVersionWatermark(datasetType) && nativeDataType === 'timestamp')
    );
};

/**
 * Returns true if the column type is date-only (no time component).
 * Date-only watermark columns only ingest data up to the end of yesterday
 * to avoid missing data due to low granularity.
 *
 * For SAP Table, ABAP type 'D' (Date) maps to logical type String,
 * so we also check the nativeDataType when the dataset is SAP Table.
 */
export const isDateOnlyColumnType = (columnType?: string, nativeDataType?: string, datasetType?: string): boolean => {
    if (datasetType === adfModelTypeMap.sapTable.datasetType) {
        return nativeDataType === 'D';
    }
    return columnType === ColumnType.Date;
};

export const SAP_TABLE_TABLE_NAME_WATERMARK_FIELD = new Map([
    ['SKA1', 'LAST_CHANGED_TS'],
    ['SKAT', 'LAST_CHANGED_TS'],
    ['SKB1', 'LAST_CHANGED_TS'],
    ['COSP', 'TIMESTMP'],
    ['COSS', 'TIMESTMP'],
    ['VBAK', 'UPD_TMSTMP'],
    ['KNB1', 'AEDAT'],
    ['EKKO', 'AEDAT'],
    ['EKPO', 'AEDAT'],
    ['EBAN', 'ERDAT']
]);
